import React, { useMemo, useState, useEffect } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { ChevronDown, ChevronUp, DollarSign, Target, Smile, Building2, HardHat, Landmark, PencilRuler, UserCog, TrendingUp, TrendingDown } from 'lucide-react'
import { DashboardCard } from './DashboardCard'
import { db } from '../../services/firebase'
import { TOP20_CLIENTS_RAW, type Top20ClientRaw } from '../../data/top20ClientsData'
import type { ClientType } from '../../data/top20ClientsData'
import {
  buildClientProfitabilityRows,
  buildProfitabilityMatrixProjects,
  filterProjectsStartedInRange,
  PROJECT_LABOUR_RATE_PER_HOUR,
  PROJECT_PROFITABILITY_STARTED_FROM,
  type ProjectProfitabilityEntry,
} from '../ProfitabilityPerformance/mockData'
import { fetchProjectProfitabilityFromFirestore } from '../../services/pwfProfitabilitySnapshotService'

export interface Top20ClientRow extends Top20ClientRaw {
  rank: number
  win_rate: number
  ranking_score: number
  projects_lost: number
  /** % change: (rolling_12m_revenue - revenue_2024) / revenue_2024; null when revenue_2024 is 0 */
  growth_12m_pct: number | null
  /** Weighted margin % from PWF profitability snapshot; null when unavailable */
  profitability_pct: number | null
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value)
}

function formatCurrencyCompact(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}m`
  if (value >= 1_000) return `$${Math.round(value / 1_000)}k`
  return new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value)
}

function npsScoreColor(score: number): string {
  if (score >= 70) return 'text-emerald-700 font-medium'
  if (score >= 50) return 'text-amber-700 font-medium'
  return 'text-red-700 font-medium'
}

const CLIENT_TYPE_STYLE: Record<ClientType, { bg: string; text: string; icon: React.ElementType }> = {
  Council: { bg: 'bg-slate-100', text: 'text-slate-800', icon: Landmark },
  Developer: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Building2 },
  Builder: { bg: 'bg-amber-100', text: 'text-amber-800', icon: HardHat },
  Architect: { bg: 'bg-violet-100', text: 'text-violet-800', icon: PencilRuler },
  Consultant: { bg: 'bg-teal-100', text: 'text-teal-800', icon: UserCog },
}

function ComingSoonBadge() {
  return (
    <span className="inline-flex items-center rounded-md bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
      Coming soon
    </span>
  )
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-400/90 text-amber-900 font-bold text-sm shadow-sm" title="1st">1</span>
  if (rank === 2) return <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-300 text-slate-800 font-bold text-sm shadow-sm" title="2nd">2</span>
  if (rank === 3) return <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-600/80 text-amber-100 font-bold text-sm shadow-sm" title="3rd">3</span>
  return <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-700 font-semibold text-sm">{rank}</span>
}

function ProjectsWonBar({ won, lost }: { won: number; lost: number }) {
  const total = won + lost || 1
  const wonPct = (won / total) * 100
  const lostPct = 100 - wonPct
  return (
    <div className="flex items-center gap-1 min-w-0">
      <div className="flex-1 min-w-0 h-1.5 rounded-full overflow-hidden flex">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${wonPct}%` }} />
        <div className="h-full bg-red-400 transition-all" style={{ width: `${lostPct}%` }} />
      </div>
      <span className="text-[10px] font-medium text-gray-700 shrink-0 tabular-nums">{won}/{lost}</span>
    </div>
  )
}

function NpsBar({ score }: { score: number }) {
  const pct = Math.min(100, Math.max(0, score))
  const barColor = score >= 70 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-500' : 'bg-red-400'
  return (
    <div className="flex items-center gap-1 min-w-0">
      <div className="flex-1 min-w-0 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-[10px] font-semibold w-5 text-right shrink-0 tabular-nums ${npsScoreColor(score)}`}>{score}</span>
    </div>
  )
}

function This12mCell({ row }: { row: Top20ClientRow }) {
  const fmt = formatCurrencyCompact(row.rolling_12m_revenue)
  return <span className="whitespace-nowrap" title={formatCurrency(row.rolling_12m_revenue)}>{fmt}</span>
}

function GrowthPctDisplay({ pct }: { pct: number | null | undefined }) {
  if (pct == null) return <span className="text-gray-400">—</span>
  const isInfinity = pct === Infinity || !Number.isFinite(pct)
  if (isInfinity && pct > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-600" title="Growth from $0 last 12M">
        <TrendingUp className="w-3 h-3 shrink-0" />
        ∞
      </span>
    )
  }
  const isUp = pct >= 0
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-medium tabular-nums ${isUp ? 'text-emerald-600' : 'text-red-600'}`}
      title="% change vs last 12 months"
    >
      {isUp ? <TrendingUp className="w-3 h-3 shrink-0" /> : <TrendingDown className="w-3 h-3 shrink-0" />}
      {isUp ? '+' : ''}
      {pct.toFixed(1)}%
    </span>
  )
}

function Growth12mCell({ row }: { row: Top20ClientRow }) {
  return <GrowthPctDisplay pct={row.growth_12m_pct} />
}

function normalizeClientName(name: string): string {
  return name.trim().toLowerCase()
}

function marginClass(marginPct: number | null): string {
  if (marginPct == null) return 'text-gray-400'
  if (marginPct >= 30) return 'text-emerald-700 font-medium'
  if (marginPct >= 0) return 'text-amber-700 font-medium'
  return 'text-red-700 font-medium'
}

function fmtProfitabilityPct(marginPct: number | null | undefined): string {
  if (marginPct == null) return '—'
  return `${marginPct.toFixed(1)}%`
}

function ProfitabilityCell({ pct }: { pct: number | null | undefined }) {
  if (pct == null) return <span className="text-gray-400">—</span>
  return (
    <span className={`tabular-nums ${marginClass(pct)}`} title="Profitability % (invoiced ex GST − tracked time cost) ÷ invoiced">
      {fmtProfitabilityPct(pct)}
    </span>
  )
}

function buildClientProfitabilityMarginByName(
  projectProfitability: readonly ProjectProfitabilityEntry[]
): Map<string, number | null> {
  const eligible = filterProjectsStartedInRange(
    projectProfitability,
    PROJECT_PROFITABILITY_STARTED_FROM,
    ''
  )
  const matrixProjects = buildProfitabilityMatrixProjects([], eligible, [])
  const clientRows = buildClientProfitabilityRows(matrixProjects, PROJECT_LABOUR_RATE_PER_HOUR)
  const map = new Map<string, number | null>()
  for (const row of clientRows) {
    map.set(normalizeClientName(row.client), row.marginPct)
  }
  return map
}

const COMING_SOON_COLUMNS = ['last_win', 'projects_won', 'win_rate', 'nps_score'] as const

const COLUMNS: { key: keyof Top20ClientRow | 'last_win' | 'last_12m'; label: string; sortKey?: keyof Top20ClientRow; format?: (row: Top20ClientRow) => string; width?: string }[] = [
  { key: 'rank', label: 'Rank', sortKey: 'rank', width: '4%' },
  { key: 'name', label: 'Client', sortKey: 'name', width: '14%' },
  { key: 'type', label: 'Type', sortKey: 'type', width: '8%' },
  { key: 'last_12m', label: 'Last 12M', sortKey: 'revenue_2024', format: (r) => formatCurrency(r.revenue_2024), width: '8%' },
  { key: 'rolling_12m_revenue', label: 'This 12M', sortKey: 'rolling_12m_revenue', format: (r) => formatCurrency(r.rolling_12m_revenue), width: '8%' },
  { key: 'growth_12m_pct', label: '% Growth', sortKey: 'growth_12m_pct', format: (r) => (r.growth_12m_pct == null ? '—' : r.growth_12m_pct === Infinity ? '∞' : `${r.growth_12m_pct >= 0 ? '+' : ''}${r.growth_12m_pct.toFixed(1)}%`), width: '6%' },
  { key: 'profitability_pct', label: 'Profit %', sortKey: 'profitability_pct', format: (r) => fmtProfitabilityPct(r.profitability_pct), width: '7%' },
  { key: 'last_win', label: 'Last Win', sortKey: 'days_since_last_win', width: '6%' },
  { key: 'projects_won', label: 'Conv.', sortKey: 'projects_won', width: '11%' },
  { key: 'win_rate', label: 'Win %', sortKey: 'win_rate', format: (r) => `${(r.win_rate * 100).toFixed(1)}%`, width: '6%' },
  { key: 'nps_score', label: 'NPS', sortKey: 'nps_score', width: '12%' },
]

function computeRankedClients(
  profitabilityByClient: Map<string, number | null>
): Top20ClientRow[] {
  const minRev = Math.min(...TOP20_CLIENTS_RAW.map((c) => c.rolling_12m_revenue))
  const maxRev = Math.max(...TOP20_CLIENTS_RAW.map((c) => c.rolling_12m_revenue))
  const minEng = Math.min(...TOP20_CLIENTS_RAW.map((c) => c.engagement_frequency))
  const maxEng = Math.max(...TOP20_CLIENTS_RAW.map((c) => c.engagement_frequency))
  const rangeRev = maxRev - minRev || 1
  const rangeEng = maxEng - minEng || 1

  const withScore: Top20ClientRow[] = TOP20_CLIENTS_RAW.map((c) => {
    const normRev = (c.rolling_12m_revenue - minRev) / rangeRev
    const normEng = (c.engagement_frequency - minEng) / rangeEng
    const ranking_score = normRev * 0.7 + normEng * 0.3
    const win_rate = c.briefed_projects > 0 ? c.projects_won / c.briefed_projects : 0
    const projects_lost = c.briefed_projects - c.projects_won
    const growth_12m_pct =
      c.revenue_2024 === 0
        ? (c.rolling_12m_revenue > 0 ? Infinity : null)
        : ((c.rolling_12m_revenue - c.revenue_2024) / c.revenue_2024) * 100
    const profitability_pct = profitabilityByClient.get(normalizeClientName(c.name)) ?? null
    return { ...c, ranking_score, win_rate, rank: 0, projects_lost, growth_12m_pct, profitability_pct }
  })

  const sorted = [...withScore].sort((a, b) => b.ranking_score - a.ranking_score)
  const top20 = sorted.slice(0, 20).map((row, i) => ({ ...row, rank: i + 1 }))
  return top20
}

const PWF_TOP20_DOC = 'pwfTop20ClientsRevenue'
const PWF_TOP20_LATEST = 'latest'

/** Row shape for table: full demo row or PWF-only (rank, name, rolling_12m_revenue, revenue_last_12m?, growth_12m_pct?, profitability_pct?) */
type TableRow = Top20ClientRow | {
  rank: number
  name: string
  rolling_12m_revenue: number
  revenue_last_12m?: number
  growth_12m_pct?: number | null
  profitability_pct?: number | null
}

function isFullRow(row: TableRow): row is Top20ClientRow {
  return 'type' in row && 'revenue_2024' in row
}

export function Top20Clients() {
  const [sortKey, setSortKey] = useState<keyof Top20ClientRow | null>(null)
  const [sortAsc, setSortAsc] = useState(true)
  const [pwfTotalRevenue, setPwfTotalRevenue] = useState<number | null>(null)
  const [pwfTableData, setPwfTableData] = useState<Array<{ companyId: number; companyName: string; revenue12Months: number; revenueThis12M?: number; revenueLast12M?: number }> | null>(null)
  const [projectProfitability, setProjectProfitability] = useState<ProjectProfitabilityEntry[]>([])

  useEffect(() => {
    let cancelled = false
    void fetchProjectProfitabilityFromFirestore()
      .then((snapshot) => {
        if (cancelled || !snapshot?.projectProfitability?.length) return
        setProjectProfitability(snapshot.projectProfitability)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const profitabilityByClient = useMemo(
    () => buildClientProfitabilityMarginByName(projectProfitability),
    [projectProfitability]
  )

  useEffect(() => {
    let cancelled = false
    const ref = doc(db, PWF_TOP20_DOC, PWF_TOP20_LATEST)
    getDoc(ref)
      .then((snap) => {
        if (cancelled || !snap.exists()) return
        const d = snap.data()
        const data = d?.data as Array<{ companyId: number; companyName: string; revenue12Months: number; revenueThis12M?: number; revenueLast12M?: number }> | undefined
        if (data?.length && !cancelled) {
          const total = data.reduce((s, c) => s + (c.revenueThis12M ?? c.revenue12Months ?? 0), 0)
          setPwfTotalRevenue(total)
          setPwfTableData(data)
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const ranked = useMemo(
    () => computeRankedClients(profitabilityByClient),
    [profitabilityByClient]
  )

  const tableRows = useMemo((): TableRow[] => {
    if (pwfTableData?.length) {
      return pwfTableData.map((c, i) => {
        const this12m = c.revenueThis12M ?? c.revenue12Months ?? 0
        const last12m = c.revenueLast12M
        const growth_12m_pct =
          last12m == null
            ? null
            : last12m === 0
              ? (this12m > 0 ? Infinity : null)
              : ((this12m - last12m) / last12m) * 100
        const profitability_pct =
          profitabilityByClient.get(normalizeClientName(c.companyName)) ?? null
        return {
          rank: i + 1,
          name: c.companyName,
          rolling_12m_revenue: this12m,
          revenue_last_12m: last12m,
          growth_12m_pct,
          profitability_pct,
        }
      })
    }
    return ranked
  }, [pwfTableData, ranked, profitabilityByClient])

  const sorted = useMemo(() => {
    if (!sortKey) return tableRows
    const copy = [...tableRows]
    copy.sort((a, b) => {
      let aVal = a[sortKey as keyof TableRow]
      let bVal = b[sortKey as keyof TableRow]
      if (sortKey === 'growth_12m_pct') {
        const aNum = aVal == null || aVal === '' ? -Infinity : Number(aVal)
        const bNum = bVal == null || bVal === '' ? -Infinity : Number(bVal)
        return sortAsc ? aNum - bNum : bNum - aNum
      }
      if (sortKey === 'profitability_pct') {
        const aNum = aVal == null || aVal === '' ? -Infinity : Number(aVal)
        const bNum = bVal == null || bVal === '' ? -Infinity : Number(bVal)
        return sortAsc ? aNum - bNum : bNum - aNum
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') return sortAsc ? aVal - bVal : bVal - aVal
      const aStr = String(aVal ?? '')
      const bStr = String(bVal ?? '')
      return sortAsc ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
    })
    return copy
  }, [tableRows, sortKey, sortAsc])

  const kpis = useMemo(() => {
    const totalRevenue = tableRows.reduce((s, c) => s + c.rolling_12m_revenue, 0)
    const fullRows = tableRows.filter(isFullRow)
    const avgNps = fullRows.length ? fullRows.reduce((s, c) => s + c.nps_score, 0) / fullRows.length : 0
    const avgWinRate = fullRows.length ? fullRows.reduce((s, c) => s + c.win_rate, 0) / fullRows.length : 0
    return { totalRevenue, avgNps, avgWinRate }
  }, [tableRows])

  const handleSort = (key: keyof Top20ClientRow) => {
    if (sortKey === key) setSortAsc((a) => !a)
    else {
      setSortKey(key)
      setSortAsc(true)
    }
  }

  const getCellValue = (row: TableRow, col: (typeof COLUMNS)[number]): string | number => {
    if (!isFullRow(row)) {
      if (col.key === 'rank') return row.rank
      if (col.key === 'name') return row.name
      if (col.key === 'rolling_12m_revenue') return row.rolling_12m_revenue
      if (col.key === 'last_12m') return row.revenue_last_12m ?? '—'
      if (col.key === 'growth_12m_pct') return row.growth_12m_pct ?? ''
      if (col.key === 'profitability_pct') return row.profitability_pct ?? ''
      return '—'
    }
    if (col.key === 'last_win') return row.days_since_last_win
    if (col.key === 'last_12m') return row.revenue_2024
    if (col.key === 'growth_12m_pct') return row.growth_12m_pct ?? ''
    if (col.format) return col.format(row)
    const v = row[col.key as keyof Top20ClientRow]
    return v ?? ''
  }

  return (
    <DashboardCard title="Top 20 Clients" accent="terracotta" className="w-full">
      <p className="text-sm text-gray-500 mb-4">
        Clients ranked by rolling 12-month revenue and engagement frequency.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200 p-4 flex items-start gap-3 shadow-sm">
          <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center shrink-0">
            <DollarSign className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-xs font-medium text-emerald-700 uppercase tracking-wider">Total Revenue From Top 20</p>
            <p className="text-2xl font-bold text-emerald-900 mt-0.5">
              {pwfTotalRevenue != null ? formatCurrency(pwfTotalRevenue) : formatCurrency(kpis.totalRevenue)}
            </p>
          </div>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200 p-4 flex items-start gap-3 shadow-sm relative opacity-60">
          <div className="absolute top-2 right-2">
            <ComingSoonBadge />
          </div>
          <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center shrink-0">
            <Smile className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-xs font-medium text-blue-700 uppercase tracking-wider">Average NPS Score</p>
            <p className="text-2xl font-bold text-blue-900 mt-0.5">{kpis.avgNps.toFixed(1)}</p>
            <div className="mt-1.5 h-1.5 w-16 bg-blue-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(100, kpis.avgNps)}%` }} />
            </div>
          </div>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-violet-50 to-violet-100/50 border border-violet-200 p-4 flex items-start gap-3 shadow-sm relative opacity-60">
          <div className="absolute top-2 right-2">
            <ComingSoonBadge />
          </div>
          <div className="w-10 h-10 rounded-lg bg-violet-500 flex items-center justify-center shrink-0">
            <Target className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-xs font-medium text-violet-700 uppercase tracking-wider">Average Win Rate</p>
            <p className="text-2xl font-bold text-violet-900 mt-0.5">{(kpis.avgWinRate * 100).toFixed(1)}%</p>
            <div className="mt-1.5 h-1.5 w-16 bg-violet-200 rounded-full overflow-hidden">
              <div className="h-full bg-violet-500 rounded-full" style={{ width: `${(kpis.avgWinRate * 100).toFixed(0)}%` }} />
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 overflow-hidden relative">
        <table className="w-full divide-y divide-gray-200 text-xs" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            {COLUMNS.map((col) => (
              <col key={String(col.key)} style={{ width: col.width ?? 'auto' }} />
            ))}
          </colgroup>
          <thead className="bg-gray-50">
            <tr>
              {COLUMNS.map((col) => {
                const key = col.sortKey ?? col.key
                const isSortable = true
                const isComingSoon = COMING_SOON_COLUMNS.includes(col.key as (typeof COMING_SOON_COLUMNS)[number])
                return (
                  <th
                    key={String(col.key)}
                    className={`px-1.5 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider truncate ${isComingSoon ? 'opacity-60' : ''} ${isSortable ? 'cursor-pointer hover:bg-gray-100 select-none' : ''}`}
                    onClick={isSortable ? () => handleSort(key as keyof Top20ClientRow) : undefined}
                    title={col.label}
                  >
                    <span className="inline-flex items-center gap-0.5">
                      {col.label}
                      {isSortable && sortKey === key && (sortAsc ? <ChevronUp className="w-3 h-3 shrink-0" /> : <ChevronDown className="w-3 h-3 shrink-0" />)}
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sorted.map((row, idx) => {
              const fullRow = isFullRow(row)
              return (
                <tr key={`${row.rank}-${row.name}-${idx}`} className={`${idx % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'} hover:bg-dashboard-terracotta/5 transition-colors`}>
                  {COLUMNS.map((col) => {
                    const val = getCellValue(row, col)
                    const isLastWin = col.key === 'last_win'
                    const isNps = col.key === 'nps_score'
                    const isRank = col.key === 'rank'
                    const isType = col.key === 'type'
                    const isName = col.key === 'name'
                    const isWinRate = col.key === 'win_rate'
                  const isRolling12m = col.key === 'rolling_12m_revenue'
                  const isLast12m = col.key === 'last_12m'
                  const isGrowth12m = col.key === 'growth_12m_pct'
                  const isProfitability = col.key === 'profitability_pct'
                  const isProjectsWon = col.key === 'projects_won'
                    const daysAgo = fullRow && isLastWin ? row.days_since_last_win : 0
                    const lastWinRed = isLastWin && fullRow && daysAgo > 60
                    const lastWinOrange = isLastWin && fullRow && daysAgo > 30 && daysAgo <= 60
                    const typeStyle = isType && fullRow ? CLIENT_TYPE_STYLE[row.type] : null
                    const TypeIcon = typeStyle?.icon

                    const isComingSoonCol = COMING_SOON_COLUMNS.includes(col.key as (typeof COMING_SOON_COLUMNS)[number])
                    return (
                      <td
                        key={String(col.key)}
                        className={`px-1.5 py-1.5 text-gray-700 min-w-0 overflow-hidden ${isComingSoonCol ? 'opacity-60' : ''} ${lastWinRed ? 'bg-red-100 text-red-900' : lastWinOrange ? 'bg-orange-100 text-orange-900' : ''}`}
                      >
                        {isRank && <RankBadge rank={row.rank} />}
                        {isName && <span className="truncate block" title={row.name}>{row.name}</span>}
                        {isType && (fullRow && typeStyle ? (
                          <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium max-w-full truncate ${typeStyle.bg} ${typeStyle.text}`} title={row.type}>
                            {TypeIcon && <TypeIcon className="w-3 h-3 shrink-0" />}
                            {row.type}
                          </span>
                        ) : '—')}
                        {isLast12m && (fullRow ? <span className="whitespace-nowrap" title={formatCurrency(row.revenue_2024)}>{formatCurrencyCompact(row.revenue_2024)}</span> : (row.revenue_last_12m != null ? <span className="whitespace-nowrap" title={formatCurrency(row.revenue_last_12m)}>{formatCurrencyCompact(row.revenue_last_12m)}</span> : '—'))}
                        {isRolling12m && (fullRow ? <This12mCell row={row} /> : (row.rolling_12m_revenue != null ? <span className="whitespace-nowrap" title={formatCurrency(row.rolling_12m_revenue)}>{formatCurrencyCompact(row.rolling_12m_revenue)}</span> : '—'))}
                        {isGrowth12m && (fullRow ? <Growth12mCell row={row} /> : <GrowthPctDisplay pct={'growth_12m_pct' in row ? (row as { growth_12m_pct?: number | null }).growth_12m_pct : undefined} />)}
                        {isProfitability && (
                          <ProfitabilityCell
                            pct={'profitability_pct' in row ? row.profitability_pct : undefined}
                          />
                        )}
                        {isProjectsWon && (fullRow ? <ProjectsWonBar won={row.projects_won} lost={row.projects_lost} /> : '—')}
                        {isWinRate && (fullRow ? <span className="tabular-nums">{(row.win_rate * 100).toFixed(1)}%</span> : '—')}
                        {isNps && (fullRow ? <NpsBar score={row.nps_score} /> : '—')}
                        {!isRank && !isType && !isName && !isWinRate && !isNps && !isRolling12m && !isLast12m && !isGrowth12m && !isProfitability && !isProjectsWon && (
                          isLastWin ? (fullRow && val !== undefined && val !== '' ? `${val}d ago` : '—') : (val !== undefined && val !== '' ? String(val) : '—')
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
        {/* Diagonal COMING SOON overlay on last 4 columns (Last Win, Conv., Win %, NPS) */}
        <div
          className="absolute right-0 top-0 bottom-0 w-[40%] flex items-center justify-center pointer-events-none"
          aria-hidden
        >
          <span className="text-lg sm:text-xl font-bold uppercase tracking-widest text-gray-300 -rotate-[-35deg] whitespace-nowrap select-none">
            Coming soon
          </span>
        </div>
      </div>
    </DashboardCard>
  )
}

export default Top20Clients
